import { useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { monthPrefix, newNoteFile, newNotePath } from '../writing/textNote';
import { newUlid } from '../entity/catalog';
import { useNoteText } from '../writing/useNoteText';
import { hrefFor } from './EntityPage';
import { Loading } from '../shell/Loading';
import '../writing/write.css';

/**
 * A text document, which is what writing on this device should feel like.
 *
 * The app already draws this line: a simple text note opens as text, and the
 * detail view with its connections is a switch away rather than the thing you
 * land in. The web only had the side panel, which is shaped like filling in a
 * form — you have to be somewhere that holds a file before you can write a
 * word. Here the page IS the file, and there is nothing to decide first.
 *
 * **The file is minted on the first word, not on arrival.** Opening the
 * editor and changing your mind should leave nothing behind; a vault full of
 * empty `Note …` files is the cost of writing the file when the screen opens.
 * Once minted the URL swaps to the note's path, so a reload comes back to it
 * and the editor stops being a separate mode.
 *
 * Saving is the write queue's job — edits coalesce per path and go out when
 * the typing stops, and the shell's sync dot already says where they are. A
 * second saved-indicator here would be a second answer to one question.
 */
export function WriteRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, write } = useVault();
  const field = useRef<HTMLTextAreaElement>(null);

  const path = decodeURIComponent(location.pathname.replace(/^\/write\/?/, ''));

  const mint = useCallback(
    (first: string) => {
      if (state.status !== 'ready') return null;
      const now = new Date();
      const taken = new Set(state.vault.listPaths(monthPrefix(now)));
      const fresh = newNotePath(now, (candidate) => taken.has(candidate));
      const file = newNoteFile(first, newUlid(now.getTime()));
      write([{ path: fresh, text: file }]);
      navigate(`/write/${encodeURIComponent(fresh)}`, { replace: true });
      return { path: fresh, raw: file };
    },
    [state, write, navigate],
  );

  const { raw, text, missing, structured, edit } = useNoteText(path || null, field, { mint });

  useEffect(() => {
    field.current?.focus();
  }, [path]);

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to write into your vault.
        </p>
      </section>
    );
  }

  if (missing) {
    return (
      <section className="placeholder">
        <h1>Not in the vault</h1>
        <p className="placeholder-phase">{path}</p>
        <p>
          <Link to="/plan">Back to the board</Link>
        </p>
      </section>
    );
  }

  if (raw === null) return <Loading title="Opening…" />;

  // A file that holds more than one message has no single run of prose to
  // edit, and flattening it here would destroy the structure the app built.
  if (structured) {
    return (
      <section className="placeholder">
        <h1>This one is not a plain text note</h1>
        <p className="t-bodyProse">
          It holds more than one piece of content — topics, a transcript, picked lines. Editing it
          as one run of text would flatten all of that.
        </p>
        <p>
          <Link to={hrefFor(path)}>Open its details</Link>
        </p>
      </section>
    );
  }

  return (
    <div className="write">
      <div className="write-head">
        <span className="t-eyebrowMono write-eyebrow">{path ? 'note' : 'new note'}</span>
        <span className="write-spacer" />
        {path && (
          <Link className="mode-switch t-chipLabel" to={hrefFor(path)}>
            details
          </Link>
        )}
      </div>
      <textarea
        ref={field}
        className="write-field t-bodyProse"
        value={text}
        placeholder="Write…"
        spellCheck
        onChange={(e) => edit(e.target.value)}
      />
    </div>
  );
}
